<script setup lang="ts">
import { computed, onMounted, type PropType, reactive, ref, watch } from "vue";
import { EntryField } from "@/domains/home/services/registry/types";
import { FormDatum } from "@/domains/home/services/nir/configurations/types/forms.ts";
import PostalAddressView from "@/domains/home/services/nir/forms/components/widgets/viewers/PostalAddressView.vue";
import AdminUnitsView from "@/domains/home/services/nir/forms/components/widgets/viewers/AdminUnitsView.vue";
import NatureOfBusinessISICView
  from "@/domains/home/services/nir/forms/components/widgets/viewers/NatureOfBusinessISICView.vue";
import ContactView from "@/domains/home/services/nir/forms/components/widgets/viewers/ContactView.vue";
import CountryView from "@/domains/home/services/nir/forms/components/widgets/viewers/CountryView.vue";
import IndividualsView from "@/domains/home/services/nir/forms/components/widgets/viewers/IndividualsView.vue";
import BeneficialOwnersView
  from "@/domains/home/services/nir/forms/components/widgets/viewers/BeneficialOwnersView.vue";
import {
  AdminUnitsSelection, FormBeneficialOwnerPerson,
  FormContact,
  FormCorporate,
  FormPerson,
  IPostalAddress,
  ISICSelection
} from "@/domains/home/services/nir/forms/components/widgets/types";
import { ApplicationData } from "@/domains/home/services/nir/registration/types";
import CorporatesView from "@/domains/home/services/nir/forms/components/widgets/viewers/CorporatesView.vue";

const props = defineProps({
  formData: Object as PropType<FormDatum>,
  valueData: Array as PropType<ApplicationData[]>,
  active: Boolean,
})

const emit = defineEmits(['submit'])

const formData = reactive<EntryField[]>([])

// Prefill form data from valueData prop
onMounted(() => {
  prefillFormData()
})

watch(() => props.valueData, () => {
  prefillFormData()
}, { deep: true })

function prefillFormData() {
  if (!props.valueData) return

  props.valueData.forEach((step) => {
   step.data.forEach((item) => {
     updateFormData(item.fieldId, item.fieldName, item.value)
   })
  })
}

// Helper function to get field value
function getFieldValue(fieldId: string) {
  const entry = formData.find(item => item.fieldId === fieldId)
  return entry?.value || ''
}

function getObjFieldValue<T>(fieldId: string) {
  const entry = formData.find(item => item.fieldId === fieldId)
  return entry?.value as T || ref<T|undefined>().value
}

// Helper function to check if a checkbox option is checked
function isCheckboxChecked(fieldId: string, optionValue?: string) {
  const value = getFieldValue(fieldId)
  if (optionValue) {
    // Multiple checkboxes - check if value array includes this option
    return Array.isArray(value) ? value.includes(optionValue) : false
  } else {
    // Single checkbox - return boolean value
    return Boolean(value)
  }
}

// Computed property to get sorted sections
const orderedSections = computed(() => {
  if (!props.formData?.sections) return []
  return [...props.formData.sections].sort((a, b) => a.position - b.position)
})

function getSortedFields(fields: any[]) {
  if (!fields) return []
  return [...fields].sort((a, b) => a.position - b.position)
}

function getSortedOptions(options: any[]) {
  if (!options || !Array.isArray(options)) return []
  return [...options].filter((opt) => opt.isActive).sort((a, b) => a.position - b.position)
}

function updateFormData(fieldId: string, fieldName: string, value: any) {
  const existingIndex = formData.findIndex((item) => item.fieldId === fieldId)

  if (existingIndex !== -1 && formData[existingIndex]) {
    formData[existingIndex].value = value
  } else {
    formData.push({
      fieldName,
      fieldId,
      value,
    })
  }
}

function handleFieldChange(event: Event, fieldId: string, fieldName: string) {
  const target = event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  updateFormData(fieldId, fieldName, target.value)
}

function handleCheckboxChange(
  event: Event,
  fieldId: string,
  fieldName: string,
  optionValue?: string,
) {
  const target = event.target as HTMLInputElement
  const existingEntry = formData.find((item) => item.fieldId === fieldId)

  if (optionValue) {
    // Multiple checkboxes - store as array
    let values: string[] = Array.isArray(existingEntry?.value) ? existingEntry.value : []
    if (target.checked) {
      values.push(optionValue)
    } else {
      values = values.filter((v) => v !== optionValue)
    }
    updateFormData(fieldId, fieldName, values)
  } else {
    // Single checkbox - store as boolean
    updateFormData(fieldId, fieldName, target.checked)
  }
}

function handleFileChange(event: Event, fieldId: string, fieldName: string) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    updateFormData(fieldId, fieldName, target.files[0])
  }
}

// Handler for custom component value changes (AdminUnitsView, ISIC, Contact, Country, etc.)
function handleComponentChange(value: any, fieldId: string, fieldName: string) {
  updateFormData(fieldId, fieldName, value)
}

function submitForm() {
  if(props.active){
    emit('submit', formData)
  }
}

</script>

<template>
  <div class="flex flex-col border border-gray-200 rounded-xl overflow-y-auto px-6 py-4">
    <!-- Form Sections -->
    <div v-if="orderedSections.length > 0" class="space-y-8">
      <div v-for="(section, sectionIndex) in orderedSections" :key="section.id" class="space-y-4">
        <!-- Section Title -->
        <div class="border-l-4 border-primary-600 pl-4 py-2">
          <h3 class="text-xl font-semibold text-gray-800">
            {{ sectionIndex + 1 }}. {{ section.name }}
          </h3>
          <p v-if="section.description" class="text-gray-600 text-sm mt-1">
            {{ section.description }}
          </p>
        </div>

        <!-- Section Fields -->
        <div v-if="section.fields && section.fields.length > 0" class="pl-6 space-y-5">
          <div
            v-for="(fieldData, fieldIndex) in getSortedFields(section.fields)"
            :key="fieldData.field.id"
            class="space-y-2"
          >
            <label class="block">
              <span class="text-sm font-medium text-gray-700">
                {{ fieldData.field.label }}
                <span v-if="fieldData.field.isMandatory" class="text-red-500">*</span>
              </span>
              <!-- Text Input -->
              <input
                v-if="
                  fieldData.field.type === 'text' ||
                  fieldData.field.type === 'email' ||
                  fieldData.field.type === 'number'
                "
                :type="fieldData.field.type.toLowerCase()"
                :value="getFieldValue(fieldData.field.id)"
                :placeholder="`Enter ${fieldData.field.label.toLowerCase()}`"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />

              <!-- Textarea -->
              <textarea
                v-else-if="
                  fieldData.field.type === 'textarea' || fieldData.field.type === 'long_text'
                "
                :value="getFieldValue(fieldData.field.id) as string"
                :placeholder="`Enter ${fieldData.field.label.toLowerCase()}`"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                rows="3"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              ></textarea>

              <!-- Select Dropdown -->
              <select
                v-else-if="fieldData.field.type === 'select' || fieldData.field.type === 'dropdown'"
                :value="getFieldValue(fieldData.field.id)"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select {{ fieldData.field.label.toLowerCase() }}</option>
                <option
                  v-for="option in getSortedOptions(fieldData.field.options)"
                  :key="option.id"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>

              <!-- Date Input -->
              <input
                v-else-if="fieldData.field.type === 'date'"
                type="date"
                :value="getFieldValue(fieldData.field.id)"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />

              <!-- Checkbox -->
              <div
                v-else-if="
                  fieldData.field.type === 'checkbox' || fieldData.field.type === 'boolean'
                "
                class="mt-2 space-y-2"
              >
                <div v-if="fieldData.field.options && fieldData.field.options.length > 0">
                  <label
                    v-for="option in getSortedOptions(fieldData.field.options)"
                    :key="option.id"
                    class="flex items-center"
                  >
                    <input
                      type="checkbox"
                      :value="option.value"
                      :checked="isCheckboxChecked(fieldData.field.id, option.value)"
                      :disabled="!fieldData.field.isActive || !active"
                      @change="
                        handleCheckboxChange(
                          $event,
                          fieldData.field.id,
                          fieldData.field.name,
                          option.value,
                        )
                      "
                      class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <span class="ml-2 text-sm text-gray-600">{{ option.label }}</span>
                  </label>
                </div>
                <label v-else class="inline-flex items-center">
                  <input
                    type="checkbox"
                    :checked="isCheckboxChecked(fieldData.field.id)"
                    :required="fieldData.field.isMandatory"
                    :disabled="!fieldData.field.isActive || !active"
                    @change="handleCheckboxChange($event, fieldData.field.id, fieldData.field.name)"
                    class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <span class="ml-2 text-sm text-gray-600">Check this box</span>
                </label>
              </div>

              <!-- Radio Buttons -->
              <div v-else-if="fieldData.field.type === 'radio'" class="mt-2 space-y-2">
                <div v-if="fieldData.field.options && fieldData.field.options.length > 0">
                  <label
                    v-for="option in getSortedOptions(fieldData.field.options)"
                    :key="option.id"
                    class="flex items-center"
                  >
                    <input
                      type="radio"
                      :name="`radio-${fieldData.field.id}`"
                      :value="option.value"
                      :checked="getFieldValue(fieldData.field.id) === option.value"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive || !active"
                      @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <span class="ml-2 text-sm text-gray-600">{{ option.label }}</span>
                  </label>
                </div>
                <div v-else>
                  <label class="inline-flex items-center">
                    <input
                      type="radio"
                      :name="`radio-${fieldData.field.id}`"
                      :required="fieldData.field.isMandatory"
                      :disabled="!fieldData.field.isActive"
                      @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <span class="ml-2 text-sm text-gray-600">Option 1</span>
                  </label>
                  <label class="inline-flex items-center ml-4">
                    <input
                      type="radio"
                      :name="`radio-${fieldData.field.id}`"
                      :disabled="!fieldData.field.isActive || !active"
                      @change="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                      class="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <span class="ml-2 text-sm text-gray-600">Option 2</span>
                  </label>
                </div>
              </div>

              <!-- File Upload -->
              <input
                v-else-if="fieldData.field.type === 'file' || fieldData.field.type === 'upload'"
                type="file"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @change="handleFileChange($event, fieldData.field.id, fieldData.field.name)"
                class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />

              <PostalAddressView
                v-else-if="fieldData.field.type === 'postal-address'"
                :data="getObjFieldValue<IPostalAddress>(fieldData.field.id)"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <AdminUnitsView
                v-else-if="fieldData.field.type === 'admin-units'"
                :data="getObjFieldValue<AdminUnitsSelection>(fieldData.field.id)"
                :active="active"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <NatureOfBusinessISICView
                v-else-if="fieldData.field.type === 'isic'"
                :data="getObjFieldValue<ISICSelection>(fieldData.field.id)"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <ContactView
                v-else-if="fieldData.field.type === 'contact'"
                :data="getObjFieldValue<FormContact[]>(fieldData.field.id)"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <CountryView
                v-else-if="fieldData.field.type === 'country'"
                :data="getFieldValue(fieldData.field.id) as string"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <IndividualsView
                v-else-if="fieldData.field.type === 'individuals'"
                :cardinality="fieldData.field.cardinality"
                :data="getObjFieldValue<FormPerson[]>(fieldData.field.id)"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />

              <CorporatesView
                  v-else-if="fieldData.field.type === 'corporates'"
                  :cardinality="fieldData.field.cardinality"
                  :data="getObjFieldValue<FormCorporate[]>(fieldData.field.id)"
                  @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <BeneficialOwnersView
                v-else-if="fieldData.field.type === 'beneficial-owners'"
                :cardinality="fieldData.field.cardinality"
                :data="getObjFieldValue<FormBeneficialOwnerPerson[]>(fieldData.field.id)"
                @update:modelValue="(value) => handleComponentChange(value, fieldData.field.id, fieldData.field.name)"
              />
              <!-- Default/Generic Input -->
              <input
                v-else
                type="text"
                :value="getFieldValue(fieldData.field.id)"
                :placeholder="`Enter ${fieldData.field.label.toLowerCase()}`"
                :required="fieldData.field.isMandatory"
                :disabled="!fieldData.field.isActive || !active"
                @input="handleFieldChange($event, fieldData.field.id, fieldData.field.name)"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />

              <!-- Field Info -->
              <div class="mt-1 flex items-center gap-3 text-xs">
                <span
                  v-if="!fieldData.field.isActive"
                  class="text-orange-600 flex items-center gap-1"
                >
                  <i class="fa-solid fa-exclamation-triangle"></i>
                  Inactive field
                </span>
              </div>
            </label>
          </div>
        </div>

        <!-- Empty Section -->
        <div v-else class="pl-6 text-gray-500 italic">No fields in this section</div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <i class="fa-solid fa-folder-open text-5xl text-gray-300 mb-4"></i>
      <p class="text-gray-500">No application data to preview</p>
    </div>

  </div>
</template>

<style scoped></style>
